import { Table, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { IChequeKizReportDTO } from '../../../../../libs/coreapi-dto/accounting/cashSession';
import styles from './TableReportStatusKiz.module.scss'

interface IKizReports {
    idChequeGlobal: string;
    chequeNumber: string;
    recipeNumber: string | null;
    gtinSgtin: string;
    localKizState: string;
    kizStateIS: string;
    schemeType: number;
    isSending: boolean;

}

const TableReportStatusKiz = ({ idReports, data, onDataChange }: { idReports: string, data: IKizReports[]; onDataChange: (serverData: IChequeKizReportDTO) => void; }) => {

    const [kizData, setKizData] = useState<IKizReports[]>(
        data.map((item, index) => ({
            ...item,
            uniqueId: `${item.idChequeGlobal}_${item.gtinSgtin}_${index}`,
        }))
    );

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: data.length,
    });

    const handleTableChange = (pagination: any) => {
        setPagination(pagination);
    };

    const transformToSchemeToReportFormat = (data: IKizReports[]): IChequeKizReportDTO => {
        const grouped = data
            .filter(item => item.isSending) // только те, что isSending === true
            .reduce<Record<string, {
                chequeNumber: string;
                recipeNumber: string;
                kizReports: IKizReports[];
            }>>((acc, item) => {
                const id = item.idChequeGlobal;
                if (!acc[id]) {
                    acc[id] = {
                        chequeNumber: item.chequeNumber,
                        recipeNumber: item.recipeNumber ?? '',
                        kizReports: []
                    };
                }
                acc[id].kizReports.push({
                    gtinSgtin: item.gtinSgtin,
                    localKizState: item.localKizState,
                    kizStateIS: item.kizStateIS,
                    schemeType: item.schemeType,
                    isSending: item.isSending ?? ''
                } as IKizReports);
                return acc;
            }, {});

        return {
            idGlobal: idReports,
            reports: Object.entries(grouped).map(([idChequeGlobal, group]) => ({
                idChequeGlobal,
                chequeNumber: group.chequeNumber,
                recipeNumber: group.recipeNumber ?? '',
                kizReports: group.kizReports
            }))
        };
    };

    useEffect(() => {
        const serverData = transformToSchemeToReportFormat(kizData);
        onDataChange(serverData);
    }, [kizData]);

    const columns: ColumnsType<IKizReports> = [
        {
            title: 'Номер чека',
            dataIndex: 'chequeNumber',
            key: 'chequeNumber',
            width: 100,
        },
        {
            title: 'Номер рецепта',
            dataIndex: 'recipeNumber',
            key: 'recipeNumber',
            width: 100,
        },
        {
            title: 'GTIN/SGTIN',
            dataIndex: 'gtinSgtin',
            key: 'gtinSgtin',
            width: 150,
        },
        {
            title: 'Статус КИЗ в БД',
            dataIndex: 'localKizState',
            key: 'localKizState',
            width: 80,
            render: (text: string) => {
                if (text === 'Exit') return 'Выбыл';
                if (text === 'Part') return 'Выбыл частично';
                return text;
            },
        },
        {
            title: 'Статус в ЧЗ',
            dataIndex: 'kizStateIS',
            key: 'kizStateIS',
            width: 80,
            render: (text: string) => {
                if (text === 'Exit') return 'Выбыл';
                if (text === 'Part') return 'Выбыл частично';
                if (text === 'Proc') return 'В обороте'; // не блокируем чекбокс
                if (text === 'Stop') return 'Приостановлен';
                return text;
            },
        },
        {
            title: 'Тип схемы',
            dataIndex: 'schemeType',
            key: 'schemeType',
            width: 80,
            render: (text: string) => {
                const schemeType: Record<string, string> = {
                    '1': 'Рецепт',
                    '0': 'Обычный'
                }
                return schemeType[text] || text;
            }
        },
        {
            title: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ marginRight: 8 }}>Отправить 511/521</span>
                    <Checkbox
                        indeterminate={
                            kizData.some(item => item.isSending) &&
                            !kizData.every(item => item.isSending)
                        }
                        checked={kizData.every(item => !item.isSending) ? false : 
                                 kizData.every(item => item.isSending || item.kizStateIS !== "Proc")}
                        onChange={(e) => {
                            const newData = kizData.map(item => ({
                                ...item,
                                isSending: item.kizStateIS === "Proc" ? e.target.checked : item.isSending
                            }));
                            setKizData(newData);
                        }}
                        disabled={!kizData.some(item => item.kizStateIS === "Proc")}
                    />
                </div>
            ),
            dataIndex: 'isSending',
            key: 'isSending',
            width: 60,
            align: 'center',
            render: (_, record) => (
                <Checkbox
                    disabled={record.kizStateIS === "Proc" ? false : true} // блокируем все кроме "В обороте"
                    checked={record.isSending}
                    onChange={(e) => {
                        const newData = kizData.map(item =>
                            item.idChequeGlobal === record.idChequeGlobal &&
                                item.gtinSgtin === record.gtinSgtin
                                ? { ...item, isSending: e.target.checked }
                                : item
                        );
                        setKizData(newData);
                    }}
                />
            ),
        },
    ];

    return (
        <div className={styles.wrapper_Table}>
            <Table
                className={styles.table}
                columns={columns}
                dataSource={kizData}
                rowKey="uniqueId"
                scroll={{ x: 1125 }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
                }}
                onChange={handleTableChange}
                bordered
                size="middle"
                sticky={{
                    offsetHeader: 0,
                }}
            />
        </div>
    );
}

export default TableReportStatusKiz